import { BayLevelEnum, ForeAftEnum } from "open-vessel-definition";
import {
  IBay40sMisplacedPair,
  ICenterLineRowConflict,
  IFixBay40sLocationResult,
  IRepairOvdResult,
  TRepairStepName,
} from "@tedivo/tedivo-ovd-validators";
import { IButtonAttributes, IntegratedDialog } from "@tedivo/tedivo-ui";
import { getForeAftEnumTranslationKey } from "@tedivo/tedivo-pure-helpers";
import { getTranslation } from "../../../../../../../app/i18/i18tn";
import ovdJsonStore from "../../../../../../../app/stores/OVDJsonStore";

type TAction = "close" | "save";

/**
 * Tells the user what was repaired in the file as it opened: Bay and slot data
 * synchronised with their keys, placeholder row weights removed, row 00 slots in Bays
 * without a centerline row, and 40'+ definitions in the wrong Bay of their pair. The
 * changes live in memory until saved.
 */
export function showOvdRepairedDialog(repair: IRepairOvdResult): void {
  const readonly = ovdJsonStore.readonlyMode;

  const buttonsAttrs: IButtonAttributes<TAction>[] = [
    {
      label: getTranslation("general:common.close"),
      value: "close",
      type: "button",
      outlined: true,
    },
  ];

  if (!readonly) {
    buttonsAttrs.push({
      label: getTranslation("view:ovdRepaired.save"),
      value: "save",
      type: "submit",
      variant: "primary",
      autofocus: true,
    });
  }

  const dialog = new IntegratedDialog<TAction>({
    parentNode: document.body,
    buttonsAttrs,
    removeDialogWhenHidden: true,
    maxWidth: "640px",
  });

  dialog.onButtonClicked = (action) => {
    // A file still being created is saved with the repairs already in it
    if (action === "save" && !ovdJsonStore.readonlyMode && ovdJsonStore.tvdId !== undefined) {
      ovdJsonStore.saveToCloud();
    }
  };

  dialog.show(getTranslation("view:ovdRepaired.title"), createContent(repair, readonly));
}

function createContent(repair: IRepairOvdResult, readonly: boolean): HTMLElement {
  const holder = document.createElement("div");
  holder.className = "ovd-repaired-dialog";

  const intro = document.createElement("p");
  intro.innerHTML = getTranslation("view:ovdRepaired.intro");
  holder.appendChild(intro);

  if (repair.bayLevelStructure.changed) {
    holder.appendChild(
      createCountSection("bayLevelStructure", repair.bayLevelStructure.fixed.length),
    );
  }

  if (repair.slotStructure.changed) {
    holder.appendChild(createCountSection("slotStructure", repair.slotStructure.fixed.length));
  }

  if (repair.rowWeights.changed) {
    holder.appendChild(createCountSection("rowWeights", repair.rowWeights.dropped.length));
  }

  if (repair.centerLineRow.changed) {
    holder.appendChild(createCenterLineRowSection(repair.centerLineRow.repaired));
  }

  if (repair.bay40s.changed) {
    holder.appendChild(createBay40sSection(repair.bay40s));
  }

  const outcome = document.createElement("p");
  outcome.innerHTML = getTranslation(
    readonly ? "view:ovdRepaired.readonly" : "view:ovdRepaired.notSaved",
  );
  holder.appendChild(outcome);

  return holder;
}

/** A step whose details are not worth listing: a title and a count */
function createCountSection(step: TRepairStepName, count: number): HTMLElement {
  const section = document.createElement("div");

  const title = document.createElement("h3");
  title.innerHTML = getTranslation(`view:ovdRepaired.${step}.title`);
  section.appendChild(title);

  const line = document.createElement("p");
  line.innerHTML = getTranslation(`view:ovdRepaired.${step}.line`, { count });
  section.appendChild(line);

  return section;
}

function createCenterLineRowSection(conflicts: ICenterLineRowConflict[]): HTMLElement {
  const section = document.createElement("div");

  const title = document.createElement("h3");
  title.innerHTML = getTranslation("view:ovdRepaired.centerLineRow.title");
  section.appendChild(title);

  const intro = document.createElement("p");
  intro.innerHTML = getTranslation("view:ovdRepaired.centerLineRow.intro");
  section.appendChild(intro);

  const list = document.createElement("ul");
  conflicts.forEach((conflict) => {
    const li = document.createElement("li");
    li.innerHTML = getTranslation(`view:ovdRepaired.centerLineRow.${conflict.repair}`, {
      isoBay: conflict.isoBay,
      level: levelName(conflict.level),
      count: conflict.slots.length,
      reason: getTranslation(`view:ovdRepaired.centerLineRow.reason.${conflict.reason}`),
    });
    list.appendChild(li);
  });
  section.appendChild(list);

  return section;
}

function createBay40sSection(fix: IFixBay40sLocationResult): HTMLElement {
  const section = document.createElement("div");

  const title = document.createElement("h3");
  title.innerHTML = getTranslation("view:ovdRepaired.bay40s.title");
  section.appendChild(title);

  const { bay40sLocation, bay40sLocationSource } = conventionOf(fix);

  const intro = document.createElement("p");
  intro.innerHTML =
    bay40sLocation !== undefined && bay40sLocationSource !== undefined
      ? getTranslation("view:ovdRepaired.bay40s.introConvention", {
          location: getTranslation(getForeAftEnumTranslationKey(bay40sLocation)),
          source: getTranslation(`view:ovdRepaired.bay40s.source.${bay40sLocationSource}`),
        })
      : getTranslation("view:ovdRepaired.bay40s.introNoConvention");
  section.appendChild(intro);

  const list = document.createElement("ul");
  fix.moved.forEach((pair) => {
    const li = document.createElement("li");
    li.innerHTML = describePair(pair);
    list.appendChild(li);
  });
  section.appendChild(list);

  if (fix.moved.some((pair) => pair.decidedBy === "pair" || pair.decidedBy === "default")) {
    const setConvention = document.createElement("p");
    setConvention.innerHTML = getTranslation("view:ovdRepaired.bay40s.setConvention");
    section.appendChild(setConvention);
  }

  return section;
}

function describePair(pair: IBay40sMisplacedPair): string {
  const notes: string[] = [];

  if (pair.split) notes.push(getTranslation("view:ovdRepaired.bay40s.split"));

  if (pair.decidedBy === "pair" || pair.decidedBy === "default") {
    notes.push(getTranslation(`view:ovdRepaired.bay40s.decidedBy.${pair.decidedBy}`));
  }

  const text = getTranslation("view:ovdRepaired.bay40s.pair", {
    fwd: pair.fwd,
    aft: pair.aft,
    level: levelName(pair.level),
    from: pair.fromIsoBay,
    to: pair.toIsoBay,
  });

  return notes.length > 0 ? `${text} <em>(${notes.join(", ")})</em>` : text;
}

function levelName(level: BayLevelEnum): string {
  return getTranslation(`enums:BayLevelEnum.${level === BayLevelEnum.ABOVE ? "ABOVE" : "BELOW"}`);
}

/** The convention the moved pairs were judged against */
function conventionOf(fix: IFixBay40sLocationResult): {
  bay40sLocation: ForeAftEnum | undefined;
  bay40sLocationSource: "shipData" | "detected" | undefined;
} {
  const first = fix.moved[0];

  if (first === undefined || first.decidedBy === "pair" || first.decidedBy === "default") {
    return { bay40sLocation: undefined, bay40sLocationSource: undefined };
  }

  return { bay40sLocation: first.to, bay40sLocationSource: first.decidedBy };
}
